import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { apiErr } from "../../lib/utils";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input, Label, Textarea } from "../ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Sparkles, RefreshCw, Plus, Trash2, ShieldCheck, History, Scale, Lightbulb, Gauge } from "lucide-react";
import { toast } from "sonner";

const PILLAR = { E: { label: "Environment", color: "#00E676" }, S: { label: "Social", color: "#4FC3F7" }, G: { label: "Governance", color: "#FFB74D" } };
const CLASS_LABEL = { material_both: "Material — impact & financial", impact_material: "Impact-material", financial_material: "Financially material", below_threshold: "Below threshold" };
const STATUS_BADGE = { approved: "success", assessed: "warning", candidate: "outline", archived: "outline" };
const RATINGS = ["Low", "Medium", "High", "Critical"];
const MAP_STATUS = { candidate: "outline", partial: "warning", validated: "success", not_applicable: "outline" };
const MAP_CYCLE = ["candidate", "partial", "validated", "not_applicable"];

function PillarDot({ p }) { return <span className="h-2 w-2 rounded-full inline-block shrink-0" style={{ background: PILLAR[p]?.color }} />; }

// ============================ Organisation & Issues ============================
export function OrganisationIssues({ onChanged }) {
  const [ctx, setCtx] = useState(null);
  const [issues, setIssues] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ title: "", pillar: "E", description: "" });

  const load = async () => {
    const [c, i] = await Promise.all([api.get("/materiality/context"), api.get("/materiality/issues")]);
    setCtx(c.data); setIssues(i.data.issues || []);
  };
  useEffect(() => { load(); }, []);

  const saveCtx = async () => {
    try { await api.put("/materiality/context", ctx); toast.success("Organisation context saved"); } catch (e) { toast.error(apiErr(e)); }
  };
  const addIssue = async () => {
    if (!form.title.trim()) return toast.warning("Enter an issue title");
    try { await api.post("/materiality/issues", form); toast.success("Candidate issue added"); setAddOpen(false); setForm({ title: "", pillar: "E", description: "" }); load(); onChanged && onChanged(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!ctx) return <div className="p-6 text-muted-foreground font-mono text-sm">Loading…</div>;
  const F = [["locations", "Operating locations"], ["workforce", "Workforce"], ["customers", "Customers"], ["suppliers", "Suppliers"], ["value_chain", "Value chain"]];
  return (
    <div className="space-y-6" data-testid="mat-view-organisation">
      <div className="card-flat p-5">
        <div className="label-eyebrow mb-3">Business context</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {F.map(([k, lbl]) => (
            <div key={k}>
              <Label>{lbl}</Label>
              <Textarea rows={2} value={ctx[k] || ""} onChange={(e) => setCtx({ ...ctx, [k]: e.target.value })} data-testid={`ctx-${k}`} />
            </div>
          ))}
        </div>
        <Button variant="primary" size="sm" className="mt-4" onClick={saveCtx} data-testid="ctx-save">Save context</Button>
      </div>

      <div className="card-flat p-5">
        <div className="flex items-center justify-between mb-3">
          <div><div className="label-eyebrow">Candidate issue register</div><p className="text-xs text-muted-foreground mt-0.5">Industry-template and custom issues. {issues.length} in register.</p></div>
          <Button variant="outline" size="sm" onClick={() => setAddOpen(true)} data-testid="add-issue-btn"><Plus className="h-3.5 w-3.5" /> Add issue</Button>
        </div>
        <div className="space-y-2">
          {issues.map((i) => (
            <div key={i.id} className="flex items-center gap-3 p-3 rounded-sm border border-border bg-surface" data-testid={`reg-issue-${i.code}`}>
              <PillarDot p={i.pillar} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{i.title}</div>
                <div className="text-[11px] font-mono text-muted-foreground">{i.code} · {PILLAR[i.pillar]?.label} · {(i.kpi_codes || []).length} metric(s)</div>
              </div>
              <Badge variant={STATUS_BADGE[i.status]} className="text-[9px] uppercase">{i.status}</Badge>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent><DialogHeader><DialogTitle>Add candidate issue</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="add-issue-title" /></div>
            <div><Label>Pillar</Label>
              <Select value={form.pillar} onValueChange={(v) => setForm({ ...form, pillar: v })}>
                <SelectTrigger data-testid="add-issue-pillar"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PILLAR).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <Button variant="primary" onClick={addIssue} data-testid="add-issue-save">Add to register</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================ Assessment & Engagement ============================
export function AssessmentEngagement({ onChanged }) {
  const [issues, setIssues] = useState([]);
  const [sel, setSel] = useState(null);
  const [a, setA] = useState({});
  const [audit, setAudit] = useState([]);
  const [dec, setDec] = useState({ classification: "impact_material", rationale: "", decided_by: "", status: "approved" });

  const loadIssues = async () => { const r = await api.get("/materiality/issues"); setIssues(r.data.issues || []); return r.data.issues || []; };
  const loadAudit = async (code) => { const r = await api.get("/materiality/audit"); setAudit((r.data.entries || []).filter((e) => e.issue_code === code)); };
  useEffect(() => { loadIssues(); }, []);

  const open = async (i) => {
    setSel(i);
    setA(i.assessment || { iros: [], evidence: [] });
    setDec({ classification: i.decision?.classification || "impact_material", rationale: "", decided_by: "", status: "approved" });
    loadAudit(i.code);
  };

  const saveAssessment = async () => {
    try { const r = await api.put(`/materiality/issues/${sel.id}/assessment`, a); toast.success("Assessment saved"); setSel(r.data); setA(r.data.assessment); loadIssues(); loadAudit(sel.code); onChanged && onChanged(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const saveDecision = async () => {
    if (!dec.rationale.trim() || !dec.decided_by.trim()) return toast.warning("Rationale and approver are required");
    try { const r = await api.post(`/materiality/issues/${sel.id}/decision`, dec); toast.success(`Decision ${dec.status}`); setSel(r.data); loadIssues(); loadAudit(sel.code); onChanged && onChanged(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const addIro = () => setA({ ...a, iros: [...(a.iros || []), { type: "impact", direction: "negative", description: "", horizon: "medium" }] });
  const addEv = () => setA({ ...a, evidence: [...(a.evidence || []), { source_type: "internal", summary: "", confidence: "medium" }] });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5" data-testid="mat-view-assessment">
      <div className="card-flat p-3 space-y-1.5 h-fit">
        <div className="label-eyebrow px-2 mb-1">Issues</div>
        {issues.map((i) => (
          <button key={i.id} onClick={() => open(i)} data-testid={`assess-pick-${i.code}`}
            className={`w-full text-left px-2.5 py-2 rounded-sm flex items-center gap-2 ${sel?.id === i.id ? "bg-accent/10 border border-accent/40" : "hover:bg-surface-hover border border-transparent"}`}>
            <PillarDot p={i.pillar} />
            <span className="flex-1 text-xs">{i.title}</span>
            <Badge variant={STATUS_BADGE[i.status]} className="text-[8px] uppercase">{i.status}</Badge>
          </button>
        ))}
      </div>

      {!sel ? <div className="card-flat p-10 text-center text-muted-foreground text-sm">Select an issue to assess its impact and financial materiality.</div> : (
        <div className="space-y-5">
          <div className="card-flat p-5">
            <div className="flex items-center gap-2 mb-3"><PillarDot p={sel.pillar} /><h3 className="font-display font-bold text-lg">{sel.title}</h3></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {[["impact", "Impact materiality", "Significance of the issue's effect on people & environment"], ["financial", "Financial materiality", "Effect on enterprise value — cost, risk, opportunity"]].map(([dim, lbl, hint]) => (
                <div key={dim} className="border border-border rounded-sm p-3">
                  <div className="text-xs font-semibold">{lbl}</div>
                  <div className="text-[10px] text-muted-foreground mb-2">{hint}</div>
                  <div className="flex items-center gap-2">
                    <Select value={a[`${dim}_rating`] || ""} onValueChange={(v) => setA({ ...a, [`${dim}_rating`]: v })}>
                      <SelectTrigger className="w-28" data-testid={`assess-${dim}-rating`}><SelectValue placeholder="Rating" /></SelectTrigger>
                      <SelectContent>{RATINGS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input type="number" min="0" max="100" className="w-24" placeholder="0-100" value={a[`${dim}_score`] ?? ""} onChange={(e) => setA({ ...a, [`${dim}_score`]: e.target.value === "" ? null : Number(e.target.value) })} data-testid={`assess-${dim}-score`} />
                    <span className="text-[10px] text-muted-foreground">matrix position</span>
                  </div>
                  <Textarea rows={2} className="mt-2" placeholder="Rationale" value={a[`${dim}_rationale`] || ""} onChange={(e) => setA({ ...a, [`${dim}_rationale`]: e.target.value })} data-testid={`assess-${dim}-rationale`} />
                </div>
              ))}
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between"><div className="label-eyebrow">Impacts, risks & opportunities <span className="text-[9px] normal-case">(negative & positive kept separate — never netted)</span></div><Button size="sm" variant="outline" onClick={addIro} data-testid="add-iro"><Plus className="h-3 w-3" /></Button></div>
              <div className="space-y-2 mt-2">
                {(a.iros || []).map((x, idx) => (
                  <div key={idx} className="flex items-center gap-2 flex-wrap" data-testid={`iro-${idx}`}>
                    <Select value={x.type} onValueChange={(v) => { const n = [...a.iros]; n[idx] = { ...x, type: v }; setA({ ...a, iros: n }); }}>
                      <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="impact">Impact</SelectItem><SelectItem value="risk">Risk</SelectItem><SelectItem value="opportunity">Opportunity</SelectItem></SelectContent>
                    </Select>
                    <Select value={x.direction} onValueChange={(v) => { const n = [...a.iros]; n[idx] = { ...x, direction: v }; setA({ ...a, iros: n }); }}>
                      <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="negative">Negative</SelectItem><SelectItem value="positive">Positive</SelectItem></SelectContent>
                    </Select>
                    <Input className="flex-1 min-w-[180px]" placeholder="Description" value={x.description} onChange={(e) => { const n = [...a.iros]; n[idx] = { ...x, description: e.target.value }; setA({ ...a, iros: n }); }} />
                    <Button size="sm" variant="ghost" onClick={() => setA({ ...a, iros: a.iros.filter((_, j) => j !== idx) })}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between"><div className="label-eyebrow">Stakeholder & expert evidence</div><Button size="sm" variant="outline" onClick={addEv} data-testid="add-evidence"><Plus className="h-3 w-3" /></Button></div>
              <div className="space-y-2 mt-2">
                {(a.evidence || []).map((x, idx) => (
                  <div key={idx} className="flex items-center gap-2 flex-wrap" data-testid={`evidence-${idx}`}>
                    <Select value={x.source_type} onValueChange={(v) => { const n = [...a.evidence]; n[idx] = { ...x, source_type: v }; setA({ ...a, evidence: n }); }}>
                      <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="stakeholder">Stakeholder</SelectItem><SelectItem value="expert">Expert</SelectItem><SelectItem value="document">Document</SelectItem><SelectItem value="internal">Internal</SelectItem></SelectContent>
                    </Select>
                    <Input className="flex-1 min-w-[180px]" placeholder="Summary" value={x.summary} onChange={(e) => { const n = [...a.evidence]; n[idx] = { ...x, summary: e.target.value }; setA({ ...a, evidence: n }); }} />
                    <Select value={x.confidence} onValueChange={(v) => { const n = [...a.evidence]; n[idx] = { ...x, confidence: v }; setA({ ...a, evidence: n }); }}>
                      <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
                    </Select>
                    <Button size="sm" variant="ghost" onClick={() => setA({ ...a, evidence: a.evidence.filter((_, j) => j !== idx) })}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Label className="text-xs">Overall evidence confidence</Label>
                <Select value={a.evidence_confidence || "medium"} onValueChange={(v) => setA({ ...a, evidence_confidence: v })}>
                  <SelectTrigger className="w-28" data-testid="assess-evidence-confidence"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="low">Low (gap)</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
                </Select>
                <span className="text-[10px] text-muted-foreground">Missing data stays a visible gap — never a favourable score.</span>
              </div>
            </div>
            <Button variant="primary" size="sm" className="mt-4" onClick={saveAssessment} data-testid="assess-save">Save assessment</Button>
          </div>

          <div className="card-flat p-5">
            <div className="label-eyebrow mb-2 inline-flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-accent" /> Materiality decision</div>
            {sel.decision && <div className="text-xs mb-3 text-muted-foreground" data-testid="current-decision">Current: <span className="text-foreground font-medium">{CLASS_LABEL[sel.decision.classification]}</span> · {sel.decision.status} · by {sel.decision.decided_by} ({sel.decision.effective_date})</div>}
            <p className="text-[10px] text-muted-foreground mb-3">Classification is an explicit recorded decision — not an average of the scores. An issue is material if impact-material OR financially material.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Classification</Label>
                <Select value={dec.classification} onValueChange={(v) => setDec({ ...dec, classification: v })}>
                  <SelectTrigger data-testid="decision-classification"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(CLASS_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Approver</Label><Input value={dec.decided_by} onChange={(e) => setDec({ ...dec, decided_by: e.target.value })} data-testid="decision-approver" /></div>
            </div>
            <div className="mt-3"><Label>Rationale</Label><Textarea rows={2} value={dec.rationale} onChange={(e) => setDec({ ...dec, rationale: e.target.value })} data-testid="decision-rationale" /></div>
            <div className="flex items-center gap-2 mt-3">
              <Select value={dec.status} onValueChange={(v) => setDec({ ...dec, status: v })}>
                <SelectTrigger className="w-40" data-testid="decision-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="proposed">Propose (await approval)</SelectItem><SelectItem value="approved">Approve now</SelectItem></SelectContent>
              </Select>
              <Button variant="primary" size="sm" onClick={saveDecision} data-testid="decision-save">Record decision</Button>
            </div>
          </div>

          <div className="card-flat p-5">
            <div className="label-eyebrow mb-2 inline-flex items-center gap-1"><History className="h-3.5 w-3.5" /> Decision & audit history</div>
            <div className="space-y-1.5" data-testid="issue-audit">
              {audit.length === 0 && <div className="text-xs text-muted-foreground">No history yet.</div>}
              {audit.map((e, idx) => (
                <div key={idx} className="text-[11px] flex gap-2"><span className="font-mono text-muted-foreground shrink-0">{(e.at || "").slice(0, 10)}</span><span className="text-muted-foreground">{e.actor}</span><span>{e.detail}</span></div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================ Issue Performance (KPI linker) ============================
export function IssuePerformance() {
  const { tenant } = useAuth();
  const [kpis, setKpis] = useState([]);
  const [issues, setIssues] = useState([]);
  const [linkIssue, setLinkIssue] = useState(null);
  const [linkCodes, setLinkCodes] = useState([]);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    const [k, i] = await Promise.all([api.get("/materiality/kpis"), api.get("/materiality/issues")]);
    setKpis(k.data.kpis || []); setIssues(i.data.issues || []);
  };
  useEffect(() => { load(); }, []);

  const recommend = async () => {
    if (!tenant?.sector) return toast.warning("Complete onboarding first to identify your sector");
    setRefreshing(true);
    try {
      const { data } = await api.post("/ai/recommend-kpis", { sector: tenant.sector });
      const byCode = new Map(data.recommended.map((r) => [r.code, r]));
      const existing = new Set(kpis.map((k) => k.code));
      const merged = kpis.map((k) => (byCode.has(k.code) ? { ...k, capture_path: byCode.get(k.code).capture_path } : k));
      data.recommended.forEach((r) => { if (!existing.has(r.code)) merged.push({ ...r, selected: true, ai_recommended: true }); });
      setKpis(merged); toast.success("AI recommendations refreshed");
    } catch (e) { toast.error(apiErr(e)); } finally { setRefreshing(false); }
  };
  const toggle = (i) => { const n = [...kpis]; n[i].selected = !n[i].selected; setKpis(n); };
  const save = async () => {
    const blanks = kpis.filter((k) => k.selected && k.capture_path === false);
    if (blanks.length > 0 && !window.confirm(`${blanks.length} selected KPI(s) have no data source configured yet and will render blank downstream. Keep them?`)) return;
    try { await api.put("/materiality/kpis", { kpis }); toast.success("Material KPI set saved"); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  const openLink = (iss) => { setLinkIssue(iss); setLinkCodes(iss.kpi_codes || []); };
  const saveLink = async () => {
    try { await api.put(`/materiality/issues/${linkIssue.id}/kpis`, { kpi_codes: linkCodes }); toast.success("Metrics linked"); setLinkIssue(null); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  const byDim = kpis.reduce((acc, k) => { (acc[k.dimension] = acc[k.dimension] || []).push(k); return acc; }, {});
  return (
    <div className="space-y-6" data-testid="mat-view-performance">
      {issues.length === 0 ? (
        <div className="card-flat p-8 text-center" data-testid="perf-empty">
          <Gauge className="h-7 w-7 text-accent mx-auto mb-3" />
          <h3 className="font-display text-xl font-bold">No issues to measure yet</h3>
          <p className="text-muted-foreground text-sm mt-2 max-w-md mx-auto">Build your candidate issue register in <span className="text-foreground font-medium">Organisation &amp; Issues</span> first. Once issues exist, link each one to the KPIs that measure it here.</p>
        </div>
      ) : (
      <div className="card-flat p-5">
        <div className="flex items-center justify-between mb-3">
          <div><div className="label-eyebrow">Issue ↔ metric links</div><p className="text-xs text-muted-foreground mt-0.5">Connect each material issue to the KPIs that measure it. One metric can support several issues.</p></div>
        </div>
        <div className="space-y-2">
          {issues.map((i) => (
            <div key={i.id} className="flex items-center gap-3 p-3 rounded-sm border border-border bg-surface" data-testid={`perf-issue-${i.code}`}>
              <PillarDot p={i.pillar} />
              <div className="flex-1 min-w-0"><div className="text-sm font-medium">{i.title}</div>
                <div className="text-[11px] font-mono text-muted-foreground">{(i.kpi_codes || []).length ? (i.kpi_codes || []).join(", ") : "No metrics linked"}</div></div>
              <Button size="sm" variant="outline" onClick={() => openLink(i)} data-testid={`perf-link-${i.code}`}>Link metrics</Button>
            </div>
          ))}
        </div>
      </div>
      )}

      <div className="card-flat p-5">
        <div className="flex items-center justify-between mb-4">
          <div><div className="label-eyebrow">Reporting KPI set (SASB {tenant?.industry_code || ""})</div><p className="text-xs text-muted-foreground mt-0.5">Your active disclosure-set picker — <span className="text-foreground">independent of issue links</span>{issues.length === 0 ? ", available even before issues are configured" : ""}.</p></div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={recommend} disabled={refreshing} data-testid="ai-recommend-btn">{refreshing ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} AI Recommend</Button>
            <Button variant="primary" size="sm" onClick={save} data-testid="save-kpis-btn">Save</Button>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {["Environment", "Social", "Governance"].map((dim) => (
            <div key={dim} data-testid={`dim-${dim.toLowerCase()}`}>
              <div className="label-eyebrow mb-2">{dim} <Badge variant="outline" className="ml-1">{(byDim[dim] || []).filter((k) => k.selected).length}</Badge></div>
              <div className="space-y-1.5">
                {(byDim[dim] || []).map((k) => {
                  const idx = kpis.findIndex((x) => x === k);
                  return (
                    <label key={k.code} className={`flex items-start gap-2 p-2.5 rounded-sm cursor-pointer border ${k.capture_path === false ? "bg-surface/40 border-dashed border-[#FFB800]/40 opacity-70" : k.selected ? "bg-accent/5 border-accent/30" : "bg-surface border-border hover:bg-surface-hover"}`} data-testid={`kpi-${k.code}`}>
                      <input type="checkbox" checked={!!k.selected} onChange={() => toggle(idx)} className="mt-1" />
                      <div className="flex-1 min-w-0"><div className="text-xs font-medium flex items-center gap-1.5 flex-wrap">{k.name}{k.ai_recommended && <Sparkles className="h-3 w-3 text-accent" />}{k.capture_path === false && <Badge variant="warning" className="text-[8px] uppercase" data-testid={`kpi-no-capture-${k.code}`}>No data source</Badge>}</div><div className="text-[10px] font-mono text-muted-foreground mt-0.5">{k.code} · {k.unit}</div></div>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={!!linkIssue} onOpenChange={(o) => !o && setLinkIssue(null)}>
        <DialogContent><DialogHeader><DialogTitle>Link metrics · {linkIssue?.title}</DialogTitle></DialogHeader>
          <div className="space-y-1.5 max-h-[50vh] overflow-y-auto">
            {kpis.map((k) => (
              <label key={k.code} className="flex items-center gap-2 p-2 rounded-sm border border-border cursor-pointer" data-testid={`link-kpi-${k.code}`}>
                <input type="checkbox" checked={linkCodes.includes(k.code)} onChange={() => setLinkCodes(linkCodes.includes(k.code) ? linkCodes.filter((c) => c !== k.code) : [...linkCodes, k.code])} />
                <span className="text-xs flex-1">{k.name}</span><span className="text-[10px] font-mono text-muted-foreground">{k.code}</span>
              </label>
            ))}
          </div>
          <Button variant="primary" onClick={saveLink} data-testid="link-save">Save links</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================ Standards & Disclosures ============================
export function StandardsDisclosures() {
  const [registry, setRegistry] = useState([]);
  const [issues, setIssues] = useState([]);
  const load = async () => { const r = await api.get("/materiality/standards"); setRegistry(r.data.registry || []); setIssues(r.data.issues || []); };
  useEffect(() => { load(); }, []);

  const cycle = async (iss, fw) => {
    const cur = (iss.standards || {})[fw] || "candidate";
    const next = MAP_CYCLE[(MAP_CYCLE.indexOf(cur) + 1) % MAP_CYCLE.length];
    try { await api.put(`/materiality/issues/${iss.id}/standards`, { framework: fw, status: next }); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-5" data-testid="mat-view-standards">
      <div className="card-flat p-4 flex flex-wrap gap-4 text-[11px] text-muted-foreground">
        <span className="inline-flex items-center gap-1.5"><Badge variant="outline" className="text-[8px] uppercase">Candidate</Badge> standards-informed</span>
        <span className="inline-flex items-center gap-1.5"><Badge variant="warning" className="text-[8px] uppercase">Partial</Badge> mapping in progress</span>
        <span className="inline-flex items-center gap-1.5"><Badge variant="success" className="text-[8px] uppercase">Validated</Badge> mapping validated</span>
        <span className="ml-auto">Claims are graded — "reporting-ready" and "compliant" are asserted only when evidence supports them (Release 2).</span>
      </div>
      <div className="card-flat p-5 overflow-x-auto">
        <div className="label-eyebrow mb-3">Issue × framework mapping <span className="text-[9px] normal-case">(click a cell to cycle status)</span></div>
        <table className="w-full text-xs">
          <thead><tr className="text-left text-muted-foreground border-b border-border"><th className="py-2 pr-3">Issue</th>{registry.map((f) => <th key={f.code} className="py-2 px-2 text-center" title={f.name}>{f.code}</th>)}</tr></thead>
          <tbody>
            {issues.map((i) => (
              <tr key={i.id} className="border-b border-border/50" data-testid={`std-row-${i.code}`}>
                <td className="py-2 pr-3"><span className="inline-flex items-center gap-1.5"><PillarDot p={i.pillar} />{i.title}</span></td>
                {registry.map((f) => {
                  const st = (i.standards || {})[f.code];
                  return <td key={f.code} className="py-2 px-2 text-center">
                    <button onClick={() => cycle(i, f.code)} data-testid={`std-cell-${i.code}-${f.code}`}>
                      {st ? <Badge variant={MAP_STATUS[st]} className="text-[8px] uppercase">{st === "not_applicable" ? "N/A" : st}</Badge> : <span className="text-muted-foreground opacity-40">—</span>}
                    </button></td>;
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card-flat p-4 text-[11px] text-muted-foreground inline-flex items-center gap-2"><Scale className="h-3.5 w-3.5" /> Full reporting-readiness gaps and disclosure export arrive in Release 2.</div>
    </div>
  );
}

// ============================ Actions & Review ============================
export function ActionsReview() {
  const [audit, setAudit] = useState([]);
  const [meth, setMeth] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ impact_threshold: 50, financial_threshold: 50, method: "", reason: "", approver: "" });

  const load = async () => {
    const [a, m] = await Promise.all([api.get("/materiality/audit"), api.get("/materiality/methodology")]);
    setAudit(a.data.entries || []); setMeth(m.data);
    if (m.data?.current) setForm((f) => ({ ...f, impact_threshold: m.data.current.impact_threshold, financial_threshold: m.data.current.financial_threshold, method: m.data.current.method }));
  };
  useEffect(() => { load(); }, []);

  const saveMeth = async () => {
    if (!form.reason.trim() || !form.approver.trim()) return toast.warning("A reason and approver are required to change methodology");
    try { await api.put("/materiality/methodology", form); toast.success("Methodology version recorded"); setOpen(false); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-5" data-testid="mat-view-actions">
      <div className="card-flat p-5">
        <div className="flex items-center justify-between mb-2">
          <div className="label-eyebrow inline-flex items-center gap-1"><Scale className="h-3.5 w-3.5 text-accent" /> Methodology & thresholds</div>
          <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="meth-change-btn">Change (requires reason)</Button>
        </div>
        {meth?.current && <div className="text-xs text-muted-foreground" data-testid="meth-current">v{meth.current.version} · impact ≥ {meth.current.impact_threshold}, financial ≥ {meth.current.financial_threshold} · effective {meth.current.effective_date} · approver {meth.current.approver || "—"}</div>}
        {meth?.current?.method && <p className="text-[11px] mt-2">{meth.current.method}</p>}
        {(meth?.history || []).length > 1 && <div className="mt-3 text-[10px] text-muted-foreground">{meth.history.length} versions retained — earlier assessments remain reproducible.</div>}
      </div>

      <div className="card-flat p-5">
        <div className="label-eyebrow mb-3 inline-flex items-center gap-1"><History className="h-3.5 w-3.5" /> Change & decision log</div>
        <div className="space-y-1.5" data-testid="global-audit">
          {audit.length === 0 && <div className="text-xs text-muted-foreground">No history yet.</div>}
          {audit.map((e, idx) => (
            <div key={idx} className="text-[11px] flex gap-2 items-baseline"><span className="font-mono text-muted-foreground shrink-0">{(e.at || "").slice(0, 10)}</span>{e.issue_code && <Badge variant="outline" className="text-[8px]">{e.issue_code}</Badge>}<span className="text-muted-foreground">{e.actor}</span><span>{e.detail}</span></div>
          ))}
        </div>
      </div>

      <div className="card-flat p-4 text-[11px] text-muted-foreground inline-flex items-center gap-2"><Lightbulb className="h-3.5 w-3.5" /> Linked initiatives, owners, funding links and scheduled reassessment triggers arrive in Release 2.</div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>Change methodology / thresholds</DialogTitle></DialogHeader>
          <p className="text-[11px] text-muted-foreground">Changing a threshold or method requires a reason, an approver and an effective date; the prior version is retained.</p>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Impact threshold</Label><Input type="number" value={form.impact_threshold} onChange={(e) => setForm({ ...form, impact_threshold: Number(e.target.value) })} data-testid="meth-impact" /></div>
            <div><Label>Financial threshold</Label><Input type="number" value={form.financial_threshold} onChange={(e) => setForm({ ...form, financial_threshold: Number(e.target.value) })} data-testid="meth-financial" /></div>
          </div>
          <div><Label>Method</Label><Textarea rows={2} value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} /></div>
          <div><Label>Reason for change</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="meth-reason" /></div>
          <div><Label>Approver</Label><Input value={form.approver} onChange={(e) => setForm({ ...form, approver: e.target.value })} data-testid="meth-approver" /></div>
          <Button variant="primary" onClick={saveMeth} data-testid="meth-save">Record new version</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
